/* eslint-disable indent */
import { BreadcrumbList } from '@/components/Breadcrumbs/BreadcrumbList';
import { BreadcrumbItem } from '@/components/Breadcrumbs/BreadcrumbItem';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '@/components/Loading/Loader';
import { useGetVehiculeCheckById } from '@/hooks/vehicule-check.hooks';
import { NotFound } from '@/components/Message/NotFound';
import { TabList } from '@/components/Tab/TabList';
import { TabItem } from '@/components/Tab/TabItem';
import { useTranslation } from 'react-i18next';
import { VehiculeCheckViewInfos } from '../components/VehiculeCheckViewInfos';
import { VehiculeCheckViewClient } from '../components/VehiculeCheckViewClient';
import { VehiculeCheckDetailedResults } from '@/modules/VehiculeCheckResult/components/VehiculeCheckDetailedResults';
import { VehiculeCheckResultsVideos } from '@/modules/VehiculeCheckResult/components/VehiculeCheckResultsVideos';
import { VehiculeCheckResultPhotos } from '@/modules/VehiculeCheckResult/components/VehiculeCheckResultPhotos';
import { useGetTab } from '@/hooks/tab';
import { ViewVehiculeCheckTab } from '@/type/ui/tab/vehiculeCheckTab';
import ModerationProvider from '@/providers/ModerationProvider';
import { HeadingButton } from '@/components/Custom/Button/HeadingButton';
import { useGetResultByVehiculeCheckId } from '@/hooks/vehicule-check-result.hooks';
import { PdfDownloadButton } from '@/components/Custom/Button/PdfDownloadButton/PdfDownloadButton';
import { DownloadIconV3 } from '@/components/SvgIcons/DownloadIconV3';
import { ResultsPdfModel } from '@/components/Pdf/Models/ResultsPdfModel';
import { useEffect, useRef, useState } from 'react';
import { useAuth, useUserRole } from '@/hooks/auth.hooks';
import { useListAllGroups } from '@/hooks/group.hooks';
import { useListAllCompanies } from '@/hooks/company.hooks';
import { useListAllBranches } from '@/hooks/branch.hooks';

export const ViewVehiculeCheckPage = () => {
  const { t } = useTranslation('inspection');
  const navigate = useNavigate();
  const { id } = useParams();
  const { loggedInUser, isLimitedRole } = useAuth();
  const [belongsToUser, setBelongsToUser] = useState(false);

  const isReadOnly = !loggedInUser || !belongsToUser;

  const canViewEquipmentsRef = useRef(false);
  const canViewDamagesRef = useRef(false);

  const canAccessPhotoTab =
    canViewEquipmentsRef.current || canViewDamagesRef.current;

  const { data, isLoading } = useGetVehiculeCheckById(`${id}`);
  const { isModerator, isAdmin, isGroupManager } = useUserRole();
  const {
    data: vehiculeCheckResults,
    isLoading: isLoadingVehiculeCheckResults,
    refetch: refetchVehiculeCheckResults,
  } = useGetResultByVehiculeCheckId(id);

  const constainsOnlyClientName =
    data?.data?.individualFirstname || data?.data?.individualLastname;

  const { data: groups } = useListAllGroups({
    userId: loggedInUser?.id,
  });
  const { data: companies } = useListAllCompanies({
    userId: loggedInUser?.id,
  });
  const { data: branches } = useListAllBranches({
    userId: loggedInUser?.id,
  });

  useEffect(() => {
    refetchVehiculeCheckResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const { currentTab, navigateToTab } = useGetTab({
    defaultTab: loggedInUser
      ? ViewVehiculeCheckTab.INFOS
      : ViewVehiculeCheckTab.DETAILED_RESULTS,
  });

  useEffect(() => {
    if (isLimitedRole) {
      const group = groups?.data?.find(
        (item) => item.id === data?.data?.group?.id
      );

      const company = companies?.data?.find(
        (item) => item.id === data?.data?.company?.id
      );

      const branch = branches?.data?.find(
        (item) => item.id === data?.data?.branch?.id
      );

      canViewEquipmentsRef.current = group?.showReportEquipments || isReadOnly;
      canViewDamagesRef.current = group?.showReportDamages || isReadOnly;

      setBelongsToUser(Boolean(group || company || branch));
    } else {
      setBelongsToUser(true);

      canViewEquipmentsRef.current = true;
      canViewDamagesRef.current = true;
    }
  }, [isLimitedRole, data, groups, companies, branches, isReadOnly]);

  /**
   * === Redirect to Detailed Results if NOT logged in and NOT within allowed tab ===
   */
  useEffect(() => {
    if (
      !loggedInUser &&
      currentTab !== ViewVehiculeCheckTab.INFOS &&
      currentTab !== ViewVehiculeCheckTab.VIDEOS &&
      currentTab !== ViewVehiculeCheckTab.DETAILED_RESULTS &&
      currentTab !== ViewVehiculeCheckTab.PHOTOS
    ) {
      navigateToTab(ViewVehiculeCheckTab.DETAILED_RESULTS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser, currentTab]);
  /**
   * === END OF Redirect to Detailed Results if NOT logged in and NOT within allowed tab ===
   */

  if (isLoading || isLoadingVehiculeCheckResults) {
    return <Loader />;
  }

  return (
    <>
      <PageHeader
        backToLink={
          loggedInUser
            ? location.pathname.startsWith('/vehicule-check')
              ? '/vehicule-checks'
              : -1
            : undefined
        }
        title={
          loggedInUser ? (
            t('theInspection')
          ) : (
            <div className="flex flex-1 items-center gap-2">
              <i className="ki-outline ki-frame text-xl text-primary text-[35px]" />
              <h1 className="text-xl text-black-071336">
                {t('theInspection')}
              </h1>
            </div>
          )
        }
        subTitle={
          loggedInUser && data?.data?.referenceNumber ? (
            <>
              <span className="font-medium">
                {t('common:referenceNumber_short_3')}
              </span>
              &nbsp;
              {data?.data?.referenceNumber}
            </>
          ) : undefined
        }
        subTitleIconClassName={loggedInUser ? 'ki-frame' : undefined}
        headingElement={
          loggedInUser ? (
            <>
              <div className="flex justify-between">
                <div>
                  <BreadcrumbList>
                    <BreadcrumbItem
                      text={t('inspectionList')}
                      linkTo="/vehicule-checks"
                    />
                    <BreadcrumbItem text={t('common:inspection')} active />
                  </BreadcrumbList>
                </div>

                {belongsToUser &&
                  !isModerator &&
                  !isLoading &&
                  !isLoadingVehiculeCheckResults &&
                  data?.data &&
                  vehiculeCheckResults?.data && (
                    <div className="flex items-center gap-3">
                      <PdfDownloadButton
                        text={t('downloadReport')}
                        leftIcon={
                          <DownloadIconV3 className="-mt-[1.5px] w-[18px] h-[20px] [&>path]:fill-light-inverse dark-mode:[&>path]:fill-gray-600" />
                        }
                        filename={`rapport-inspection-${id}`}
                        pdfModel={<ResultsPdfModel t={t} />}
                      />
                      {import.meta.env.MODE === 'development' && (
                        <HeadingButton
                          text={t('viewPdfReport')}
                          leftIcon={
                            <i className="ki-outline ki-eye !text-[17px] text-light-inverse dark-mode:text-gray-600" />
                          }
                          onClick={() =>
                            navigate(
                              `/vehicule-check/preview/results/pdf/${id}`
                            )
                          }
                        />
                      )}
                    </div>
                  )}
              </div>
            </>
          ) : undefined
        }
      />
      {!data?.data ? (
        <div className="mt-[95px]">
          <NotFound text={`${t('noInspectionFound')}...`} />
        </div>
      ) : (
        <>
          <div>
            {!isModerator && (
              <TabList className="mb-5">
                <TabItem
                  text={t('common:informations')}
                  leftIcon={<i className="ki-outline ki-badge" />}
                  active={currentTab === ViewVehiculeCheckTab.INFOS}
                  onChange={() => navigateToTab(ViewVehiculeCheckTab.INFOS)}
                />
                {!isReadOnly && constainsOnlyClientName && (
                  <TabItem
                    text={t('common:client')}
                    leftIcon={<i className="ki-outline ki-user" />}
                    active={currentTab === ViewVehiculeCheckTab.CLIENT}
                    onChange={() => navigateToTab(ViewVehiculeCheckTab.CLIENT)}
                  />
                )}
                <TabItem
                  text={t('common:videos')}
                  leftIcon={<i className="ki-outline ki-youtube" />}
                  active={currentTab === ViewVehiculeCheckTab.VIDEOS}
                  onChange={() => navigateToTab(ViewVehiculeCheckTab.VIDEOS)}
                />
                <TabItem
                  text={t('detailedResults')}
                  leftIcon={<i className="ki-outline ki-notepad" />}
                  active={currentTab === ViewVehiculeCheckTab.DETAILED_RESULTS}
                  onChange={() =>
                    navigateToTab(ViewVehiculeCheckTab.DETAILED_RESULTS)
                  }
                />
                {canAccessPhotoTab && (
                  <TabItem
                    text={t('common:photos')}
                    leftIcon={<i className="ki-outline ki-graph-3" />}
                    active={currentTab === ViewVehiculeCheckTab.PHOTOS}
                    onChange={() => navigateToTab(ViewVehiculeCheckTab.PHOTOS)}
                  />
                )}
              </TabList>
            )}
            {currentTab === ViewVehiculeCheckTab.INFOS && (
              <VehiculeCheckViewInfos
                t={t}
                data={data?.data}
                isReadOnly={isReadOnly}
                constainsOnlyClientName={constainsOnlyClientName}
              />
            )}
            {!isReadOnly &&
              currentTab === ViewVehiculeCheckTab.CLIENT &&
              constainsOnlyClientName && (
                <VehiculeCheckViewClient t={t} data={data?.data} />
              )}
            {currentTab === ViewVehiculeCheckTab.VIDEOS && (
              <VehiculeCheckResultsVideos t={t} />
            )}
            {currentTab === ViewVehiculeCheckTab.DETAILED_RESULTS && (
              <ModerationProvider>
                <VehiculeCheckDetailedResults
                  t={t}
                  isReadOnly={isReadOnly}
                  isModerator={isModerator || isAdmin || isGroupManager}
                  isModerationMandatory={data?.data?.group?.moderationMandatory}
                  moderationStatus={data?.data?.humanModerationStatus}
                  canViewEquipments={canViewEquipmentsRef.current}
                  canViewDamages={canViewDamagesRef.current}
                />
              </ModerationProvider>
            )}
            {currentTab === ViewVehiculeCheckTab.PHOTOS &&
              canAccessPhotoTab && (
                <ModerationProvider>
                  <VehiculeCheckResultPhotos
                    t={t}
                    isReadOnly={isReadOnly}
                    moderationStatus={data?.data?.humanModerationStatus}
                    canViewEquipments={canViewEquipmentsRef.current}
                    canViewDamages={canViewDamagesRef.current}
                  />
                </ModerationProvider>
              )}
          </div>
        </>
      )}
    </>
  );
};
